import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import DistrictDetailModal from '../../components/DistrictDetailModal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, RadarChart, Radar, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Cell, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, MapPin, AlertTriangle, CheckCircle, 
  Award, Target, Calendar, Edit, Trash2, Plus, Save, X 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AVTDashboardV2 = () => {
  // State management
  const [activeTab, setActiveTab] = useState('state-overview');
  const [reportingPeriod, setReportingPeriod] = useState('October 2025');
  const [reportingPeriods, setReportingPeriods] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);

  // District detail modal state
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Data states for each page
  const [stateOverview, setStateOverview] = useState(null);
  const [districtComparison, setDistrictComparison] = useState(null);
  const [topBottomDistricts, setTopBottomDistricts] = useState(null);
  const [roleAnalysis, setRoleAnalysis] = useState(null);
  const [roleComparison, setRoleComparison] = useState(null);
  const [selectedRole, setSelectedRole] = useState('po');
  const [timeSeries, setTimeSeries] = useState(null);
  const [trendAnalysis, setTrendAnalysis] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [interventionsSummary, setInterventionsSummary] = useState(null);
  const [bestPractices, setBestPractices] = useState(null);
  const [bottomPerformersAnalysis, setBottomPerformersAnalysis] = useState(null);
  const [allDistrictsInsights, setAllDistrictsInsights] = useState(null);
  const [editingIntervention, setEditingIntervention] = useState(null);
  const [newIntervention, setNewIntervention] = useState({
    district_id: '',
    issue_identified: '',
    intervention_planned: '',
    responsible_officer: '',
    target_date: '',
    status: 'Pending',
    remarks: ''
  });

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (reportingPeriod) {
      fetchDashboardData();
    }
  }, [reportingPeriod, activeTab, selectedRole]);

  const fetchInitialData = async () => {
    try {
      const [periodsRes, districtsRes] = await Promise.all([
        axios.get(`${API}/api/dashboards/avt-v2/reporting-periods`),
        axios.get(`${API}/api/dashboards/avt-v2/districts`)
      ]);
      setReportingPeriods(periodsRes.data.periods);
      setDistricts(districtsRes.data.districts);
      if (periodsRes.data.periods.length > 0) {
        setReportingPeriod(periodsRes.data.periods[periodsRes.data.periods.length - 1]);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'state-overview') {
        const [overviewRes, topBottomRes, bestPracticesRes] = await Promise.all([
          axios.get(`${API}/api/dashboards/avt-v2/state-overview?reporting_period=${reportingPeriod}`),
          axios.get(`${API}/api/dashboards/avt-v2/district-comparison/top-bottom?reporting_period=${reportingPeriod}&limit=5`),
          axios.get(`${API}/api/dashboards/avt-v2/best-practices?reporting_period=${reportingPeriod}&top_n=3`)
        ]);
        setStateOverview(overviewRes.data);
        setTopBottomDistricts(topBottomRes.data);
        setBestPractices(bestPracticesRes.data);
      } else if (activeTab === 'district-comparison') {
        const comparisonRes = await axios.get(`${API}/api/dashboards/avt-v2/district-comparison?reporting_period=${reportingPeriod}`);
        setDistrictComparison(comparisonRes.data);
      } else if (activeTab === 'insights-actions') {
        const [allInsightsRes, allDistrictsRes, bottomPerformersRes] = await Promise.all([
          axios.get(`${API}/api/dashboards/avt-v2/all-districts-insights?reporting_period=${reportingPeriod}`),
          axios.get(`${API}/api/dashboards/avt-v2/district-comparison?reporting_period=${reportingPeriod}`),
          axios.get(`${API}/api/dashboards/avt-v2/bottom-performers-analysis?reporting_period=${reportingPeriod}&bottom_n=10`)
        ]);
        setBottomPerformersAnalysis(bottomPerformersRes.data);
        setDistrictComparison(allDistrictsRes.data);
        // Store all districts insights in a new state
        setAllDistrictsInsights(allInsightsRes.data);
      } else if (activeTab === 'role-drilldown') {
        const [roleRes, roleCompRes] = await Promise.all([
          axios.get(`${API}/api/dashboards/avt-v2/role-analysis?role=${selectedRole}&reporting_period=${reportingPeriod}`),
          axios.get(`${API}/api/dashboards/avt-v2/role-comparison?reporting_period=${reportingPeriod}`)
        ]);
        setRoleAnalysis(roleRes.data);
        setRoleComparison(roleCompRes.data);
      } else if (activeTab === 'time-series') {
        const [timeSeriesRes, trendRes] = await Promise.all([
          axios.get(`${API}/api/dashboards/avt-v2/time-series?metric=awc_visit_pct_10days`),
          axios.get(`${API}/api/dashboards/avt-v2/trend-analysis?reporting_period=${reportingPeriod}`)
        ]);
        setTimeSeries(timeSeriesRes.data);
        setTrendAnalysis(trendRes.data);
      } else if (activeTab === 'interventions') {
        const [interventionsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/api/dashboards/avt-v2/interventions`),
          axios.get(`${API}/api/dashboards/avt-v2/interventions/summary`)
        ]);
        setInterventions(interventionsRes.data.interventions);
        setInterventionsSummary(summaryRes.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntervention = async () => {
    try {
      await axios.post(`${API}/api/dashboards/avt-v2/interventions`, newIntervention);
      setNewIntervention({
        district_id: '',
        issue_identified: '',
        intervention_planned: '',
        responsible_officer: '',
        target_date: '',
        status: 'Pending',
        remarks: ''
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error creating intervention:', error);
    }
  };

  const handleUpdateIntervention = async (interventionId, updatedData) => {
    try {
      await axios.put(`${API}/api/dashboards/avt-v2/interventions/${interventionId}`, updatedData);
      setEditingIntervention(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating intervention:', error);
    }
  };

  const handleDeleteIntervention = async (interventionId) => {
    if (window.confirm('Are you sure you want to delete this intervention?')) {
      try {
        await axios.delete(`${API}/api/dashboards/avt-v2/interventions/${interventionId}`);
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting intervention:', error);
      }
    }
  };

  const getPerformanceColor = (rating) => {
    const colors = {
      'Excellent': '#22c55e',
      'Good': '#facc15',
      'Average': '#f97316',
      'Needs Improvement': '#ef4444'
    };
    return colors[rating] || '#6b7280';
  };

  const getRoleColor = (role) => {
    const colors = {
      'PO': '#3b82f6',
      'CDPO': '#8b5cf6',
      'Supervisor': '#10b981',
      'AWC': '#f59e0b'
    };
    return colors[role] || '#6b7280';
  };

  if (loading && !stateOverview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Field Monitoring & AVT Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 pt-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Field Monitoring & AVT Dashboard</h1>
          <p className="text-gray-600">
            Comprehensive monitoring system for PO, CDPO, Supervisor, and AWC field visits across Gujarat
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4 items-center">
          <div className="w-64">
            <Label>Reporting Period</Label>
            <Select value={reportingPeriod} onValueChange={setReportingPeriod}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Select Period" />
              </SelectTrigger>
              <SelectContent>
                {reportingPeriods.map(period => (
                  <SelectItem key={period} value={period}>{period}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white grid grid-cols-6 w-full">
            <TabsTrigger value="state-overview">State Overview</TabsTrigger>
            <TabsTrigger value="district-comparison">District Comparison</TabsTrigger>
            <TabsTrigger value="role-drilldown">Role Drilldown</TabsTrigger>
            <TabsTrigger value="time-series">Time Series</TabsTrigger>
            <TabsTrigger value="insights-actions">Insights & Actions</TabsTrigger>
            <TabsTrigger value="interventions">Interventions</TabsTrigger>
          </TabsList>

          {/* ============================================ */}
          {/* PAGE 1: STATE OVERVIEW */}
          {/* ============================================ */}
          <TabsContent value="state-overview" className="space-y-6">
            {stateOverview && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-full bg-blue-100">
                          <MapPin className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Total Districts</h3>
                      <p className="text-3xl font-bold">{stateOverview.summary.total_districts}</p>
                      <p className="text-sm text-gray-500 mt-1">Covering Gujarat</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-full bg-green-100">
                          <Users className="w-6 h-6 text-green-600" />
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Total AWCs</h3>
                      <p className="text-3xl font-bold">{stateOverview.summary.total_awc.toLocaleString()}</p>
                      <p className="text-sm text-gray-500 mt-1">Anganwadi Centers</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-full bg-purple-100">
                          <Target className="w-6 h-6 text-purple-600" />
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Avg Visit Rate</h3>
                      <p className="text-3xl font-bold">{stateOverview.awc_metrics.avg_visit_pct}%</p>
                      <p className="text-sm text-gray-500 mt-1">AWC Coverage</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-full bg-orange-100">
                          <Award className="w-6 h-6 text-orange-600" />
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Top Performers</h3>
                      <p className="text-3xl font-bold">{stateOverview.performance_breakdown.excellent}</p>
                      <p className="text-sm text-gray-500 mt-1">Excellent districts</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Role Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* PO Metrics */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">PO Performance</CardTitle>
                      <CardDescription>Programme Officers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Target (10 days)</span>
                          <span className="font-semibold">{stateOverview.po_metrics.target_10days}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Visited</span>
                          <span className="font-semibold text-blue-600">{stateOverview.po_metrics.visited_10days}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Achievement</span>
                            <span className="text-xl font-bold text-blue-600">{stateOverview.po_metrics.achievement_rate}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(stateOverview.po_metrics.achievement_rate, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* CDPO Metrics */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">CDPO Performance</CardTitle>
                      <CardDescription>Child Development Officers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Target (10 days)</span>
                          <span className="font-semibold">{stateOverview.cdpo_metrics.target_10days}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Visited</span>
                          <span className="font-semibold text-purple-600">{stateOverview.cdpo_metrics.visited_10days}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Achievement</span>
                            <span className="text-xl font-bold text-purple-600">{stateOverview.cdpo_metrics.achievement_rate}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-purple-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(stateOverview.cdpo_metrics.achievement_rate, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Supervisor Metrics */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Supervisor Performance</CardTitle>
                      <CardDescription>Field Supervisors</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Target (10 days)</span>
                          <span className="font-semibold">{stateOverview.supervisor_metrics.target_10days}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Visited</span>
                          <span className="font-semibold text-green-600">{stateOverview.supervisor_metrics.visited_10days}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Achievement</span>
                            <span className="text-xl font-bold text-green-600">{stateOverview.supervisor_metrics.achievement_rate}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(stateOverview.supervisor_metrics.achievement_rate, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* AWC Metrics */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">AWC Performance</CardTitle>
                      <CardDescription>Anganwadi Centers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Target (10 days)</span>
                          <span className="font-semibold">{stateOverview.awc_metrics.target_10days}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Visited</span>
                          <span className="font-semibold text-orange-600">{stateOverview.awc_metrics.visited_10days}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Achievement</span>
                            <span className="text-xl font-bold text-orange-600">{stateOverview.awc_metrics.achievement_rate}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-orange-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(stateOverview.awc_metrics.achievement_rate, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top & Bottom Performers */}
                {topBottomDistricts && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Top 5 Performing Districts</CardTitle>
                        <CardDescription>Highest AWC visit coverage</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {topBottomDistricts.top_performers.map((district, idx) => (
                            <div
                              key={idx}
                              onClick={() => {
                                setSelectedDistrict(district);
                                setIsModalOpen(true);
                              }}
                              className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors duration-200 hover:shadow-md"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white font-bold">
                                  {idx + 1}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{district.district}</p>
                                  <p className="text-sm text-gray-500">{district.total_awc} AWCs</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-green-600">{district.value}%</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Bottom 5 Performing Districts</CardTitle>
                        <CardDescription>Need immediate attention</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {topBottomDistricts.bottom_performers.map((district, idx) => (
                            <div
                              key={idx}
                              onClick={() => {
                                setSelectedDistrict(district);
                                setIsModalOpen(true);
                              }}
                              className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors duration-200 hover:shadow-md"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-600 text-white font-bold">
                                  {idx + 1}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{district.district}</p>
                                  <p className="text-sm text-gray-500">{district.total_awc} AWCs</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-red-600">{district.value}%</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Best Practices */}
                {bestPractices && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-yellow-500" />
                        Best Practices from Top Performers
                      </CardTitle>
                      <CardDescription>Auto-identified success patterns and strategies</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {bestPractices.best_practices.map((practice, idx) => (
                          <div key={idx} className="p-4 border-l-4 border-yellow-500 bg-yellow-50 rounded-r-lg">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">
                                  #{practice.rank} {practice.district}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  Overall Performance: <span className="font-bold text-yellow-600">{practice.overall_performance}%</span>
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                                  PO: {practice.key_metrics.po_visit_pct}%
                                </span>
                                <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                                  CDPO: {practice.key_metrics.cdpo_visit_pct}%
                                </span>
                                <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                                  Supervisor: {practice.key_metrics.supervisor_visit_pct}%
                                </span>
                                <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">
                                  AWC: {practice.key_metrics.awc_visit_pct}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-gray-700">Key Success Factors:</p>
                              <ul className="list-disc list-inside space-y-1">
                                {practice.insights.map((insight, i) => (
                                  <li key={i} className="text-sm text-gray-600">{insight}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Bottom Performers Analysis */}
                {bottomPerformersAnalysis && (
                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        Improvement Plan for Bottom Performers
                      </CardTitle>
                      <CardDescription>
                        Insights, recommendations & action items for {bottomPerformersAnalysis.total_analyzed} underperforming districts
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {bottomPerformersAnalysis.bottom_performers_analysis.map((analysis, idx) => (
                          <div key={idx} className="p-5 border-l-4 border-red-500 bg-red-50 rounded-r-lg">
                            {/* District Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">
                                  #{analysis.rank} {analysis.district}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  Overall Performance: <span className="font-bold text-red-600">{analysis.overall_performance}%</span>
                                  {analysis.performance_summary.weakest_role && (
                                    <span className="ml-3 text-xs">
                                      Weakest: {analysis.performance_summary.weakest_role} ({analysis.performance_summary.weakest_role_pct}%)
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                                  PO: {analysis.key_metrics.po_visit_pct}%
                                </span>
                                <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                                  CDPO: {analysis.key_metrics.cdpo_visit_pct}%
                                </span>
                                <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                                  Supervisor: {analysis.key_metrics.supervisor_visit_pct}%
                                </span>
                                <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">
                                  AWC: {analysis.key_metrics.awc_visit_pct}%
                                </span>
                              </div>
                            </div>

                            {/* Insights */}
                            <div className="mb-4">
                              <h5 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                Key Insights
                              </h5>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                {analysis.insights.map((insight, i) => (
                                  <li key={i} className="text-sm text-gray-700">{insight}</li>
                                ))}
                              </ul>
                            </div>

                            {/* Recommendations */}
                            <div className="mb-4">
                              <h5 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-blue-500" />
                                Recommendations
                              </h5>
                              <ul className="list-disc list-inside space-y-1 ml-2">
                                {analysis.recommendations.map((rec, i) => (
                                  <li key={i} className="text-sm text-gray-700">{rec}</li>
                                ))}
                              </ul>
                            </div>

                            {/* Action Items */}
                            <div>
                              <h5 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                Action Items
                              </h5>
                              <div className="space-y-2 ml-2">
                                {analysis.action_items.map((action, i) => (
                                  <div key={i} className="p-3 bg-white rounded border border-gray-200">
                                    <div className="flex items-start justify-between mb-1">
                                      <div className="flex items-center gap-2">
                                        <span 
                                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                                            action.priority === 'High' ? 'bg-red-100 text-red-700' :
                                            action.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-blue-100 text-blue-700'
                                          }`}
                                        >
                                          {action.priority} Priority
                                        </span>
                                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">
                                          {action.role}
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-500">{action.timeline}</span>
                                    </div>
                                    <h6 className="text-sm font-semibold text-gray-900 mb-1">{action.action}</h6>
                                    <p className="text-xs text-gray-600 mb-1">{action.description}</p>
                                    <p className="text-xs text-gray-500">
                                      <span className="font-medium">Responsible:</span> {action.responsible}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ============================================ */}
          {/* PAGE 2: DISTRICT COMPARISON */}
          {/* ============================================ */}
          <TabsContent value="district-comparison" className="space-y-6">
            {districtComparison && (
              <Card>
                <CardHeader>
                  <CardTitle>District-wise Performance Comparison</CardTitle>
                  <CardDescription>
                    Ranking of all {districtComparison.total_count} districts by field visit coverage. Click on any district row to view detailed insights and analysis.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">AWCs</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">PO %</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CDPO %</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Supervisor %</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">AWC %</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg %</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rating</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {districtComparison.districts.map((district, idx) => (
                          <tr 
                            key={idx} 
                            className="hover:bg-blue-50 cursor-pointer transition-colors"
                            onClick={() => {
                              setSelectedDistrict({ district: district.district_id });
                              setIsModalOpen(true);
                            }}
                          >
                            <td className="px-4 py-3 font-bold text-gray-700">{district.rank}</td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {district.district_id}
                              <span className="ml-2 text-xs text-blue-600">(Click to view details)</span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{district.total_awc}</td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold" style={{ color: getRoleColor('PO') }}>
                                {district.metrics.po.visit_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold" style={{ color: getRoleColor('CDPO') }}>
                                {district.metrics.cdpo.visit_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold" style={{ color: getRoleColor('Supervisor') }}>
                                {district.metrics.supervisor.visit_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold" style={{ color: getRoleColor('AWC') }}>
                                {district.metrics.awc.visit_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-bold">{district.overall_avg_pct}%</td>
                            <td className="px-4 py-3 text-center">
                              <span 
                                className="px-2 py-1 text-xs font-medium rounded-full"
                                style={{ 
                                  backgroundColor: `${getPerformanceColor(district.performance_rating)}20`,
                                  color: getPerformanceColor(district.performance_rating)
                                }}
                              >
                                {district.performance_rating}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ============================================ */}
          {/* PAGE 3: ROLE DRILLDOWN */}
          {/* ============================================ */}
          <TabsContent value="role-drilldown" className="space-y-6">
            {/* Role Selector */}
            <div className="flex gap-4">
              <div className="w-64">
                <Label>Select Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="po">Programme Officer (PO)</SelectItem>
                    <SelectItem value="cdpo">Child Development Officer (CDPO)</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="awc">Anganwadi Center (AWC)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Role Summary */}
            {roleAnalysis && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Target (Month)</h3>
                      <p className="text-3xl font-bold">{roleAnalysis.summary.total_target_month}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Target (10 Days)</h3>
                      <p className="text-3xl font-bold">{roleAnalysis.summary.total_target_10days}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Visited (10 Days)</h3>
                      <p className="text-3xl font-bold text-green-600">{roleAnalysis.summary.total_visited_10days}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Achievement Rate</h3>
                      <p className="text-3xl font-bold text-blue-600">{roleAnalysis.summary.achievement_rate}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* District-wise breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>{roleAnalysis.role} Performance by District</CardTitle>
                    <CardDescription>Detailed breakdown across {roleAnalysis.summary.total_districts} districts</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Target (10d)</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Visited</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gap</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Visit %</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Performance</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {roleAnalysis.districts.slice(0, 15).map((district, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{district.district}</td>
                              <td className="px-4 py-3 text-right">{district.target_10days}</td>
                              <td className="px-4 py-3 text-right font-semibold text-green-600">{district.visited_10days}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={district.gap > 0 ? 'text-red-600' : 'text-green-600'}>
                                  {district.gap}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-bold">{district.visit_pct}%</td>
                              <td className="px-4 py-3 text-center">
                                <span 
                                  className="px-2 py-1 text-xs font-medium rounded-full"
                                  style={{ 
                                    backgroundColor: `${getPerformanceColor(district.performance)}20`,
                                    color: getPerformanceColor(district.performance)
                                  }}
                                >
                                  {district.performance}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Role Comparison Chart */}
            {roleComparison && (
              <Card>
                <CardHeader>
                  <CardTitle>Cross-Role Performance Comparison</CardTitle>
                  <CardDescription>Comparing achievement rates across all hierarchical levels</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={roleComparison.comparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="role" />
                      <YAxis label={{ value: 'Visit Percentage (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="visit_pct" name="Visit %" radius={[8, 8, 0, 0]}>
                        {roleComparison.comparison.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ============================================ */}
          {/* PAGE 4: TIME SERIES */}
          {/* ============================================ */}
          <TabsContent value="time-series" className="space-y-6">
            {timeSeries && (
              <Card>
                <CardHeader>
                  <CardTitle>Performance Trends Over Time</CardTitle>
                  <CardDescription>AWC visit coverage across reporting periods</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={timeSeries.time_series}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis label={{ value: 'Visit Percentage (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        name="Visit %"
                        dot={{ fill: '#3b82f6', r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {trendAnalysis && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(trendAnalysis.trends).map(([metric, data], idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <CardTitle className="text-base">
                        {metric.replace('_', ' ').toUpperCase().replace('10DAYS', '(10 Days)')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Average</span>
                          <span className="font-semibold">{data.average}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Highest</span>
                          <span className="font-semibold text-green-600">{data.max}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Lowest</span>
                          <span className="font-semibold text-red-600">{data.min}%</span>
                        </div>
                        <div className="flex justify-between border-t pt-3">
                          <span className="text-sm text-gray-600">Range</span>
                          <span className="font-bold">{data.range}%</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                          <div className="text-center p-2 bg-green-50 rounded">
                            <p className="text-lg font-bold text-green-600">{data.districts_above_avg}</p>
                            <p className="text-xs text-gray-600">Above Avg</p>
                          </div>
                          <div className="text-center p-2 bg-red-50 rounded">
                            <p className="text-lg font-bold text-red-600">{data.districts_below_avg}</p>
                            <p className="text-xs text-gray-600">Below Avg</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ============================================ */}
          {/* PAGE 5: INSIGHTS & ACTION ITEMS */}
          {/* ============================================ */}
          <TabsContent value="insights-actions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  District Insights & Action Items
                </CardTitle>
                <CardDescription>
                  Click on any district to view detailed insights, recommendations, and action items in the modal
                </CardDescription>
              </CardHeader>
              <CardContent>
                {allDistrictsInsights && districtComparison ? (
                  <div className="space-y-4">
                    {/* All Districts List */}
                    <div>
                      <h3 className="text-lg font-semibold mb-2">All Districts - Click to View Insights & Action Items</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        All {allDistrictsInsights.total_districts} districts have personalized insights, recommendations, and action items generated from real performance data.
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overall %</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">PO %</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CDPO %</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Supervisor %</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">AWC %</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Insights</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {districtComparison.districts.map((district, idx) => {
                              // Check if district has insights (from all districts insights)
                              const districtInsights = allDistrictsInsights?.districts_insights?.find(
                                d => d.district === district.district_id
                              );
                              const hasAnalysis = districtInsights && (
                                districtInsights.insights.length > 0 || 
                                districtInsights.recommendations.length > 0 || 
                                districtInsights.action_items.length > 0
                              );
                              const isBottomPerformer = bottomPerformersAnalysis?.bottom_performers_analysis?.some(
                                a => a.district === district.district_id
                              );
                              return (
                                <tr 
                                  key={idx} 
                                  className={`hover:bg-gray-50 cursor-pointer ${hasAnalysis ? 'bg-red-50' : ''}`}
                                  onClick={() => {
                                    setSelectedDistrict({ district: district.district_id });
                                    setIsModalOpen(true);
                                  }}
                                >
                                  <td className="px-4 py-3 font-bold text-gray-700">{district.rank}</td>
                                  <td className="px-4 py-3 font-medium text-gray-900">
                                    {district.district_id}
                                    {isBottomPerformer && (
                                      <span className="ml-2 px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">
                                        Needs Attention
                                      </span>
                                    )}
                                    {hasAnalysis && !isBottomPerformer && (
                                      <span className="ml-2 px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                                        Has Insights
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right font-bold">{district.overall_avg_pct}%</td>
                                  <td className="px-4 py-3 text-right">
                                    <span className="font-semibold" style={{ color: getRoleColor('PO') }}>
                                      {district.metrics.po.visit_pct}%
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <span className="font-semibold" style={{ color: getRoleColor('CDPO') }}>
                                      {district.metrics.cdpo.visit_pct}%
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <span className="font-semibold" style={{ color: getRoleColor('Supervisor') }}>
                                      {district.metrics.supervisor.visit_pct}%
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <span className="font-semibold" style={{ color: getRoleColor('AWC') }}>
                                      {district.metrics.awc.visit_pct}%
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {hasAnalysis ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 font-medium">
                                          {districtInsights.insights.length} Insights
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {districtInsights.action_items.length} Actions
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDistrict({ district: district.district_id });
                                        setIsModalOpen(true);
                                      }}
                                    >
                                      View Details
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Bottom Performers Summary */}
                    {bottomPerformersAnalysis.bottom_performers_analysis.length > 0 && (
                      <Card className="mt-6">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Bottom Performers Requiring Immediate Attention
                          </CardTitle>
                          <CardDescription>
                            {bottomPerformersAnalysis.total_analyzed} districts with performance below threshold
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {bottomPerformersAnalysis.bottom_performers_analysis.map((analysis, idx) => (
                              <Card 
                                key={idx} 
                                className="border-l-4 border-red-500 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => {
                                  setSelectedDistrict({ district: analysis.district });
                                  setIsModalOpen(true);
                                }}
                              >
                                <CardHeader className="pb-3">
                                  <CardTitle className="text-base">
                                    #{analysis.rank} {analysis.district}
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    <div>
                                      <span className="text-xs text-gray-600">Overall Performance:</span>
                                      <span className="ml-2 text-lg font-bold text-red-600">
                                        {analysis.overall_performance}%
                                      </span>
                                    </div>
                                    <div className="flex gap-1 flex-wrap">
                                      <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                                        PO: {analysis.key_metrics.po_visit_pct}%
                                      </span>
                                      <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                                        CDPO: {analysis.key_metrics.cdpo_visit_pct}%
                                      </span>
                                      <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
                                        Sup: {analysis.key_metrics.supervisor_visit_pct}%
                                      </span>
                                      <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700">
                                        AWC: {analysis.key_metrics.awc_visit_pct}%
                                      </span>
                                    </div>
                                    <div className="pt-2 border-t">
                                      <div className="text-xs text-gray-600">
                                        <span className="font-medium">{analysis.insights.length}</span> Insights • 
                                        <span className="font-medium"> {analysis.recommendations.length}</span> Recommendations • 
                                        <span className="font-medium"> {analysis.action_items.length}</span> Action Items
                                      </div>
                                    </div>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="w-full mt-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDistrict({ district: analysis.district });
                                        setIsModalOpen(true);
                                      }}
                                    >
                                      View Full Analysis
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Loading district insights...
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================================ */}
          {/* PAGE 6: INTERVENTIONS TRACKER */}
          {/* ============================================ */}
          <TabsContent value="interventions" className="space-y-6">
            {/* Summary Cards */}
            {interventionsSummary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Total Interventions</h3>
                    <p className="text-3xl font-bold">{interventionsSummary.total}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Pending</h3>
                    <p className="text-3xl font-bold text-orange-600">{interventionsSummary.pending}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">In Progress</h3>
                    <p className="text-3xl font-bold text-blue-600">{interventionsSummary.in_progress}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Completed</h3>
                    <p className="text-3xl font-bold text-green-600">{interventionsSummary.completed}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Add New Intervention Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add New Intervention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>District</Label>
                    <Select 
                      value={newIntervention.district_id} 
                      onValueChange={(value) => setNewIntervention({...newIntervention, district_id: value})}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select District" />
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map(district => (
                          <SelectItem key={district} value={district}>{district}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Responsible Officer</Label>
                    <Input 
                      value={newIntervention.responsible_officer}
                      onChange={(e) => setNewIntervention({...newIntervention, responsible_officer: e.target.value})}
                      placeholder="Officer name"
                    />
                  </div>
                  <div>
                    <Label>Issue Identified</Label>
                    <Input 
                      value={newIntervention.issue_identified}
                      onChange={(e) => setNewIntervention({...newIntervention, issue_identified: e.target.value})}
                      placeholder="Describe the issue"
                    />
                  </div>
                  <div>
                    <Label>Intervention Planned</Label>
                    <Input 
                      value={newIntervention.intervention_planned}
                      onChange={(e) => setNewIntervention({...newIntervention, intervention_planned: e.target.value})}
                      placeholder="Planned action"
                    />
                  </div>
                  <div>
                    <Label>Target Date</Label>
                    <Input 
                      type="date"
                      value={newIntervention.target_date}
                      onChange={(e) => setNewIntervention({...newIntervention, target_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={newIntervention.status} 
                      onValueChange={(value) => setNewIntervention({...newIntervention, status: value})}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Remarks</Label>
                    <Input 
                      value={newIntervention.remarks}
                      onChange={(e) => setNewIntervention({...newIntervention, remarks: e.target.value})}
                      placeholder="Additional notes"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button onClick={handleCreateIntervention} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Intervention
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Interventions List */}
            <Card>
              <CardHeader>
                <CardTitle>Existing Interventions</CardTitle>
                <CardDescription>Track and manage all district-level interventions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intervention</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Officer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target Date</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {interventions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                            No interventions recorded yet. Create your first intervention above.
                          </td>
                        </tr>
                      ) : (
                        interventions.map((intervention, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{intervention.district_id}</td>
                            <td className="px-4 py-3 text-sm">{intervention.issue_identified}</td>
                            <td className="px-4 py-3 text-sm">{intervention.intervention_planned}</td>
                            <td className="px-4 py-3 text-sm">{intervention.responsible_officer}</td>
                            <td className="px-4 py-3 text-sm">{intervention.target_date}</td>
                            <td className="px-4 py-3 text-center">
                              <span 
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  intervention.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                  intervention.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-orange-100 text-orange-800'
                                }`}
                              >
                                {intervention.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingIntervention(intervention)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteIntervention(intervention.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
    
    {/* District Detail Modal */}
    <DistrictDetailModal
      district={selectedDistrict}
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        setSelectedDistrict(null);
      }}
      reportingPeriod={reportingPeriod}
    />
  </div>
  );
};

export default AVTDashboardV2;
